import clsx from "clsx";
import { type ComponentProps, useCallback, useMemo, useState } from "react";

import { articleBlockSchema } from "@features/article-content/model/section-list";
import { HeroBannerSlider } from "@features/hero-banner-slider";

import { StickyCard } from "@entities/sticky-card";

import type { StrapiMedia } from "@shared/content-types/schemas/media.schema";
import { useIntersectionObserver } from "@shared/hooks/use-intersection-observer";
import { Button } from "@shared/ui/button";
import { Container } from "@shared/ui/container";
import {
  type BlockWithIdAndComponent,
  DynamicZoneRenderer,
} from "@shared/ui/dynamic-zone-renderer";
import { Icon } from "@shared/ui/icon";
import { Body } from "@shared/ui/typography";

import type { AccommodationsPointType } from "@/_pages/accommodations/model/types";
import type { CafesRestaurantsPointType } from "@/_pages/cafes-restaurants/model/types";
import type { EcotrapType } from "@/_pages/ecotrap/model";
import type { ExcursionType } from "@/_pages/excursion/model";

import { AccommodationsContent } from "./accommodations-content/accommodations-content";
import { CafesRestaurantsContent } from "./cafes-restaurants-content/cafes-restaurants-content";
import { EcotrapContent } from "./ecotrap-content";
import { ExcursionContent } from "./excursion-content";

import s from "./point-inner-content.module.scss";

export type CompProps = ComponentProps<"div"> & {
  className?: string;
  data:
    | EcotrapType
    | ExcursionType
    | CafesRestaurantsPointType
    | AccommodationsPointType;
  onButtonClick?: () => void;
};

type ContentType = {
  type: "ecotrap" | "excursion" | "cafes-restaurants" | "accommodations";
  data: CompProps["data"];
};

const getContent = (
  data: ContentType,
  isTablet?: boolean,
  onButtonClick?: () => void,
) => {
  switch (data.type) {
    case "ecotrap":
      return (
        <EcotrapContent data={data.data as EcotrapType} isTablet={isTablet} />
      );

    case "excursion":
      return (
        <ExcursionContent
          data={data.data as ExcursionType}
          isTablet={isTablet}
          onButtonClick={onButtonClick}
        />
      );

    case "cafes-restaurants":
      return (
        <CafesRestaurantsContent
          data={data.data as CafesRestaurantsPointType}
          isTablet={isTablet}
        />
      );

    case "accommodations":
      return (
        <AccommodationsContent
          data={data.data as AccommodationsPointType}
          isTablet={isTablet}
        />
      );

    default:
      return;
  }
};

export const PointInnerContent = (props: CompProps) => {
  const { className, data, onButtonClick } = props;

  const [ref, inView] = useIntersectionObserver<HTMLDivElement>();

  const contentBlocks = Array.isArray(data.content) ? data.content : [];
  const validBlocks = contentBlocks.filter(
    (block): block is Exclude<typeof block, { id: undefined }> =>
      block.id !== undefined,
  ) as BlockWithIdAndComponent[];

  const [isOpen, setOpen] = useState(false);

  const handleOpenNavigation = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  const currentContent: ContentType = useMemo(() => {
    if ("ecotrap_type" in data) {
      return { type: "ecotrap" as const, data: data as EcotrapType };
    }

    if ("cafes_restaurants_types" in data) {
      return {
        type: "cafes-restaurants" as const,
        data: data as CafesRestaurantsPointType,
      };
    }

    if ("accommodations_type" in data) {
      return {
        type: "accommodations" as const,
        data: data as AccommodationsPointType,
      };
    }

    return { type: "excursion" as const, data: data as ExcursionType };
  }, [data]);

  const galleryImages: StrapiMedia[] =
    "gallery" in data && data.gallery?.length
      ? data.gallery
      : "photos" in data && data.photos?.length
        ? data.photos
        : [];

  const hasValidBlocks = validBlocks.length > 0;
  const hasGalleryImages = galleryImages.length > 0;

  return (
    <Container tag="section" className={clsx(s.root, className)}>
      <div className={s.wrapper}>
        {hasGalleryImages && <HeroBannerSlider images={galleryImages} />}
        <div className={s.infoContentWrapper}>
          <div className={s.infoContent} ref={ref}>
            {getContent(currentContent, true, onButtonClick)}
          </div>
        </div>
        {hasValidBlocks && (
          <div className={s.content}>
            <DynamicZoneRenderer
              blocksData={validBlocks}
              blocksSchema={articleBlockSchema}
            />
          </div>
        )}
      </div>
      <StickyCard
        isOpen={isOpen}
        viewButton
        className={clsx(s.navCard, {
          [s.open]: isOpen,
          [s.viewContact]: !inView,
        })}
      >
        <Button
          className={s.mobileButton}
          data-inner-element="mobileButton"
          onClick={handleOpenNavigation}
        >
          <Body size="uppercase" weight="semiBold">
            контактная информация
          </Body>
          <Icon
            name="arrow-down"
            size={24}
            className={clsx(s.arrowIcon, { [s.arrowOpen]: isOpen })}
          />
        </Button>
        {getContent(currentContent, false, onButtonClick)}
      </StickyCard>
    </Container>
  );
};

PointInnerContent.displayName = "PointInnerContent";
